/**
 * Flow maths, kept pure so it is tested apart from the screen.
 *
 * A flow is curves over time -- energy level, BPM, danceability -- that the
 * engine curates songs to follow. This file holds what the page does with them:
 * reading a curve (the engine's own rule: linear between anchors, flat beyond),
 * editing anchors, turning the engine's picks into a continuous listen with
 * crossfades and optional speed changes, and deciding what stays when the curves
 * change while the flow is playing.
 */

/** A volume ramp, in seconds of the flow: where it starts and where it ends. */
export interface Fade {
  start: number;
  end: number;
}

export type Metric = "energy" | "bpm" | "danceability";
export type FlowMode = "set" | "endless" | "repeat";

export interface MetricInfo {
  label: string;
  min: number;
  max: number;
  /** Anchors snap to this. */
  step: number;
  color: string;
  format: (v: number) => string;
}

/** The ranges are the engine's (``scheduling.flow.ATTRIBUTES``). */
export const METRICS: Record<Metric, MetricInfo> = {
  energy: { label: "Energy", min: 1, max: 5, step: 0.1, color: "var(--curve-energy)", format: (v) => v.toFixed(1) },
  bpm: { label: "BPM", min: 60, max: 200, step: 1, color: "var(--curve-bpm)", format: (v) => v.toFixed(0) },
  danceability: { label: "Danceability", min: 0, max: 1, step: 0.01, color: "var(--curve-dance)", format: (v) => v.toFixed(2) },
};
export const METRIC_ORDER: Metric[] = ["energy", "bpm", "danceability"];

export interface CurvePoint {
  t: number;
  v: number;
}

export interface FlowRules {
  artistMinutes: number;
  titleMinutes: number;
  maxBpmStep: number;
  strictness: number;
  crossfade: number;
  stretch: boolean;
  maxStretch: number;
  variation: number;
  /** Leave out jingles, station IDs and clips under 30 seconds. */
  songsOnly: boolean;
  /** Play the clip rules between its songs (off unless its maker turns it on). */
  clipRules?: boolean;
}

/** A stretch of a flow its maker set: its own name, length (seconds) and curve. */
export interface FlowSection {
  name: string;
  length: number;
}

export interface FlowSpec {
  length: number;
  /**
   * The flow's time split into sections, in order; each is its own curve (the
   * points inside it). Together at most `length`: the time they leave holds the
   * last section's pace (and on the radio, Auto DJ's). None: one curve, as before.
   */
  sections?: FlowSection[];
  mode: FlowMode;
  metrics: Metric[];
  curves: Partial<Record<Metric, CurvePoint[]>>;
  /** The compiled search the engine filters by; ``input`` and ``filters`` restore the page. */
  query?: { clauses: unknown[]; free: unknown[] };
  input?: string;
  filters?: unknown;
  seedTrackId?: number | null;
  seedLabel?: string | null;
  rules: FlowRules;
}

export interface FlowPick {
  trackId: string;
  title: string | null;
  artist: string | null;
  /** Listening second the song starts at, and how long it has the air (speed included). */
  start: number;
  air: number;
  rate: number;
  cueIn: number;
  cueOut: number;
  duration: number;
  /** The mix point in force, when the song has one: it hands over there (``air`` already ends there). */
  mix?: number | null;
  bpm: number | null;
  energyLevel: number | null;
  danceability: number | null;
  camelot: string | null;
  targets: Partial<Record<Metric, number>>;
  match: number | null;
  transition: { harmonic?: boolean; keyRelation?: string } | null;
  /** Placed by hand: stays where it is when the flow is re-chosen. */
  pinned?: boolean;
}

export const DEFAULT_RULES: FlowRules = {
  artistMinutes: 45,
  titleMinutes: 180,
  maxBpmStep: 8,
  strictness: 0.8,
  crossfade: 6,
  stretch: false,
  maxStretch: 0.06,
  variation: 0,
  songsOnly: true,
  clipRules: false,
};

export function defaultSpec(): FlowSpec {
  return {
    length: 3600,
    mode: "set",
    metrics: ["energy"],
    curves: {
      energy: [{ t: 0, v: 2 }, { t: 2400, v: 4.5 }, { t: 3600, v: 3 }],
      bpm: [{ t: 0, v: 100 }, { t: 3600, v: 118 }],
      danceability: [{ t: 0, v: 0.6 }, { t: 3600, v: 0.8 }],
    },
    seedTrackId: null,
    rules: { ...DEFAULT_RULES },
  };
}

/** A curve's value at ``t``: linear between anchors, flat beyond the ends (the engine's rule). */
export function valueAt(points: CurvePoint[] | undefined, t: number): number | null {
  if (!points?.length) return null;
  if (t <= points[0].t) return points[0].v;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (t <= b.t) return a.v + ((t - a.t) / (b.t - a.t || 1)) * (b.v - a.v);
  }
  return points[points.length - 1].v;
}

/** How much of the flow its sections fill (seconds). */
export const sectionTotal = (sections: FlowSection[] | undefined) => (sections ?? []).reduce((n, s) => n + s.length, 0);

/** Where each section starts and ends (seconds), in order. */
export function sectionBounds(sections: FlowSection[] | undefined): { name: string; start: number; end: number }[] {
  let start = 0;
  return (sections ?? []).map((s) => {
    const out = { name: s.name, start, end: start + s.length };
    start = out.end;
    return out;
  });
}

/**
 * A curve's value at ``t`` with sections (the engine's section_value_at): each
 * section its own curve, never joined to the one before; past the last section
 * its end value holds; a section with no points of this curve follows the whole.
 */
export function sectionValueAt(points: CurvePoint[] | undefined, sections: FlowSection[] | undefined, t: number): number | null {
  const bounds = sectionBounds(sections);
  if (!bounds.length) return valueAt(points, t);
  for (let i = 0; i < bounds.length; i++) {
    const { start, end } = bounds[i];
    const last = i === bounds.length - 1;
    if (t < end || last) {
      const at = Math.min(t, end);
      const inside = (points ?? []).filter((p) => (p.t >= start && p.t < end) || (last && p.t === end));
      return inside.length ? valueAt(inside, at) : valueAt(points, at);
    }
  }
  return valueAt(points, t);
}

/**
 * The curve as drawn: one run of points per section (it jumps at a border),
 * then a flat run for the time the sections leave. Without sections, one run
 * from 0 to the length.
 */
export function curveRuns(spec: Pick<FlowSpec, "length" | "sections" | "curves">, m: Metric): CurvePoint[][] {
  const pts = spec.curves[m] ?? [];
  if (!pts.length) return [];
  const bounds = sectionBounds(spec.sections);
  if (!bounds.length) return [[{ t: 0, v: pts[0].v }, ...pts, { t: spec.length, v: pts[pts.length - 1].v }]];
  const runs: CurvePoint[][] = bounds.map(({ start, end }, i) => {
    const last = i === bounds.length - 1;
    const inside = pts.filter((p) => (p.t >= start && p.t < end) || (last && p.t === end));
    const at = (t: number) => sectionValueAt(pts, spec.sections, Math.min(t, end - 1e-6)) ?? 0;
    return [{ t: start, v: at(start) }, ...inside, { t: end, v: at(end) }];
  });
  const filled = bounds[bounds.length - 1].end;
  if (filled < spec.length) {
    const v = sectionValueAt(pts, spec.sections, filled) ?? 0;
    runs.push([{ t: filled, v }, { t: spec.length, v }]);
  }
  return runs;
}

/** Where on the curves listening second ``t`` falls: repeat loops, the others hold at the end. */
export function curveMoment(spec: Pick<FlowSpec, "length" | "mode">, t: number): number {
  return spec.mode === "repeat" ? ((t % spec.length) + spec.length) % spec.length : Math.min(t, spec.length);
}

const snap = (value: number, step: number) => Math.round(value / step) * step;

export function clampPoint(metric: Metric, length: number, p: CurvePoint): CurvePoint {
  const m = METRICS[metric];
  return {
    t: Math.round(Math.min(length, Math.max(0, p.t))),
    v: Number(Math.min(m.max, Math.max(m.min, snap(p.v, m.step))).toFixed(3)),
  };
}

/** Add an anchor; returns the new list and the index it landed at. */
export function addPoint(points: CurvePoint[], p: CurvePoint): { points: CurvePoint[]; index: number } {
  const next = [...points, p].sort((a, b) => a.t - b.t);
  return { points: next, index: next.indexOf(p) };
}

/** Move anchor ``i``, never past its neighbours, so the order (and the drag) holds. */
export function movePoint(points: CurvePoint[], i: number, p: CurvePoint): CurvePoint[] {
  const lo = i > 0 ? points[i - 1].t + 1 : 0;
  const hi = i < points.length - 1 ? points[i + 1].t - 1 : Number.POSITIVE_INFINITY;
  const next = [...points];
  next[i] = { t: Math.min(hi, Math.max(lo, p.t)), v: p.v };
  return next;
}

export function removePoint(points: CurvePoint[], i: number): CurvePoint[] {
  return points.filter((_, k) => k !== i);
}

/** Stretch or squeeze the curves when the flow's length changes, so their shape holds. */
export function rescaleCurves(curves: FlowSpec["curves"], from: number, to: number): FlowSpec["curves"] {
  const out: FlowSpec["curves"] = {};
  for (const metric of METRIC_ORDER) {
    const points = curves[metric];
    if (points) out[metric] = points.map((p) => ({ t: Math.round((p.t / from) * to), v: p.v }));
  }
  return out;
}

// ---------------------------------------------------------------- listening

export interface FlowEntry {
  /** Stable while the pick stays: the same song at the same second. */
  key: string;
  index: number;
  trackId: string;
  title: string | null;
  listenStart: number;
  listenHandover: number;
  listenEnd: number;
  /** File second at ``listenStart`` (the Cue In), and the playback speed. */
  fileFrom: number;
  rate: number;
  fadeIn: Fade | null;
  fadeOut: Fade | null;
}

export interface FlowPlan {
  entries: FlowEntry[];
  total: number;
}

export const pickKey = (p: Pick<FlowPick, "trackId" | "start">) => `${p.trackId}@${p.start.toFixed(2)}`;

/**
 * The picks as one continuous listen. Each song starts at its pick's second and
 * hands over at its Early Out; its tail plays under the next song for up to
 * ``crossfade`` seconds, fading out while the next fades in. With no crossfade
 * the songs butt-join at the Early Out. Speed changes scale every file length.
 * A song with a mix point (Liquidsoap-style cues) hands over there and its tail
 * plays under the next until its Early Out (core/handover.ts).
 */
export function flowPlan(picks: FlowPick[], crossfade: number): FlowPlan {
  const entries: FlowEntry[] = [];
  picks.forEach((p, i) => {
    const rate = p.rate || 1;
    const handover = p.start + p.air;
    const natural = p.start + Math.max(0, p.duration - p.cueIn) / rate;
    const tail = p.mix != null && crossfade > 0 ? p.start + Math.max(0, p.cueOut - p.cueIn) / rate : handover + Math.max(0, crossfade);
    const end = Math.max(handover, Math.min(natural, tail));
    const previous = entries[entries.length - 1];
    entries.push({
      key: pickKey(p),
      index: i,
      trackId: p.trackId,
      title: p.title,
      listenStart: p.start,
      listenHandover: handover,
      listenEnd: end,
      fileFrom: p.cueIn,
      rate,
      fadeIn: previous && previous.listenEnd > p.start + 0.05 ? { start: p.start, end: previous.listenEnd } : null,
      fadeOut: end > handover + 0.05 ? { start: handover, end } : null,
    });
  });
  return { entries, total: entries.reduce((m, e) => Math.max(m, e.listenEnd), 0) };
}

/** The song that has the air at ``t`` (the last one started), or -1. */
export function flowEntryAt(plan: FlowPlan, t: number): number {
  let found = -1;
  for (let i = 0; i < plan.entries.length; i++) {
    if (plan.entries[i].listenStart <= t + 1e-6) found = i;
    else break;
  }
  return found;
}

/**
 * What stays when a playing flow is re-chosen: every song up to and including
 * the one playing (queue index *playing*). The engine continues from where that
 * song hands over, so the next song is the first new one.
 */
export function keptThrough(picks: FlowPick[], playing: number): { kept: FlowPick[]; from: number } {
  const kept = playing >= 0 ? picks.slice(0, playing + 1) : [];
  const last = kept[kept.length - 1];
  return { kept, from: last ? last.start + last.air : 0 };
}

/** Start times again after songs were moved: each starts when the one before hands over. */
export function retime(picks: FlowPick[], from = picks.length ? Math.min(...picks.map((p) => p.start)) : 0): FlowPick[] {
  let at = from;
  return picks.map((p) => {
    const next = { ...p, start: at };
    at += p.air;
    return next;
  });
}

/**
 * A new answer, with the songs placed by hand kept where they were.
 *
 * Every pinned song keeps its place in the order; the slots between them are
 * filled from `fresh` in order. A song pinned elsewhere, or one taken out
 * (`removed`), is never chosen again. Leftover fresh songs go on the end.
 */
export function mergePinned(old: FlowPick[], fresh: FlowPick[], removed: Set<string> = new Set()): FlowPick[] {
  const pinnedIds = new Set(old.filter((p) => p.pinned).map((p) => p.trackId));
  const pool = fresh.filter((p) => !pinnedIds.has(p.trackId) && !removed.has(p.trackId));
  const out: FlowPick[] = [];
  let k = 0;
  for (const p of old) {
    if (p.pinned) out.push(p);
    else if (k < pool.length) out.push(pool[k++]);
  }
  while (k < pool.length) out.push(pool[k++]);
  return retime(out, fresh[0]?.start ?? old[0]?.start ?? 0);
}

/** The picks as the engine's history: what has played, oldest first. */
export const asHistory = (picks: FlowPick[]) =>
  picks.map((p) => ({ trackId: p.trackId, start: p.start, rate: p.rate }));

export function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
}
